import React from 'react';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxItemWithDescription,
    ComboboxList,
    ComboboxOptions,
    ComboboxTrigger,
} from '@schuettflix/planum-react';
import { useTranslation } from 'react-i18next';
import { ComboboxOption } from './types.ts';
import { cn } from '@/shared/utils/cn.ts';

export type { ComboboxOption as SelectComboboxOption };

type SelectComboboxProps<TOption extends ComboboxOption = ComboboxOption> = {
    value: TOption['value'] | undefined;
    onValueChange: (value: TOption['value']) => void;
    options: TOption[];
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    error?: string | boolean;
    size?: 'md' | 'lg';
    dataTest?: string;
    className?: string;
    // ComboboxTrigger doesn't forward a ref, so this wrapper is what RHF's shouldFocusError can focus to scroll to it.
    fieldRef?: React.Ref<HTMLDivElement>;
};

export function SelectCombobox<TOption extends ComboboxOption = ComboboxOption>({
    value,
    onValueChange,
    options,
    disabled,
    label,
    placeholder,
    error,
    size = 'lg',
    dataTest,
    className,
    fieldRef,
}: SelectComboboxProps<TOption>) {
    const { t } = useTranslation();

    return (
        <Combobox<TOption>
            value={value}
            onValueChange={newValue => {
                // Prevent toggle-deselect: clicking the already-selected item should not clear the field
                if (newValue === undefined) return;
                onValueChange(newValue);
            }}
            options={options}
            disabled={disabled}
        >
            <div ref={fieldRef} tabIndex={-1} className={cn('focus:outline-none', className)}>
                <ComboboxTrigger
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    size={size}
                    dataTest={dataTest}
                    className="w-full"
                />
            </div>
            <ComboboxContent>
                <ComboboxList>
                    <ComboboxEmpty>{t('errorMessages.common.noResults')}</ComboboxEmpty>
                    <ComboboxOptions<TOption>>
                        {option => (
                            <ComboboxItemWithDescription
                                key={option.value}
                                value={option.value}
                                label={option.label}
                                description={option.description}
                            />
                        )}
                    </ComboboxOptions>
                </ComboboxList>
            </ComboboxContent>
        </Combobox>
    );
}
